<script setup lang="ts">
import { ASSISTANT_MESSAGE_MAX_CHARS, type Proposal, type TaskProposal } from '@cockpit/shared';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { usePlan } from '../composables/usePlan';
import { useAssistantStore } from '../stores/assistant';
import { useAuthStore } from '../stores/auth';
import { useFeedbackPanelStore } from '../stores/feedbackPanel';
import { usePlanStore } from '../stores/plan';
import AssistantTaskForm from './AssistantTaskForm.vue';
import '../styles/assistant.css';

/**
 * Tiroir de l'assistant (maquette `#drawer`) : Claude lit les données mais n'exécute rien. Ses
 * propositions sont des boutons : un plan s'ouvre dans l'écran de validation habituel, une tâche dans
 * un formulaire prérempli.
 */
const store = useAssistantStore();
const auth = useAuthStore();
const { openPlan } = usePlan();
const planStore = usePlanStore();
const feedbackPanel = useFeedbackPanelStore();
const question = ref('');
const input = ref<HTMLInputElement | null>(null);
const list = ref<HTMLElement | null>(null);
/** Tâche proposée en cours d'ajout (clé du message + rang de la proposition). */
const taskOpen = ref<string | null>(null);
const added = ref<Set<string>>(new Set());

const firstName = computed(() => auth.me?.name.split(' ')[0] ?? '');
const greeting = computed(
  () =>
    `Bonjour${firstName.value ? ` ${firstName.value}` : ''}. Je connais vos devis, factures, abonnements, dépenses, l'agenda et l'état de chaque projet. Je lis les données mais je n'exécute rien. Que veux-tu savoir ?`,
);

watch(
  () => store.open,
  async (open) => {
    if (open) {
      await nextTick();
      input.value?.focus();
    }
  },
);
watch(
  () => store.entries.map((e) => e.text.length).join(','),
  async () => {
    await nextTick();
    if (list.value) list.value.scrollTop = list.value.scrollHeight;
  },
);

async function send(text: string): Promise<void> {
  question.value = '';
  await store.ask(text);
}

async function onProposal(proposal: Proposal, id: string): Promise<void> {
  if (proposal.kind === 'plan') {
    await openPlan(proposal.planKind, proposal.refs);
    return;
  }
  if (proposal.kind === 'feedback') {
    // Lot AD, lot AQ : le panneau « Faire un retour » s'ouvre prérempli (il ferme l'assistant), conversation
    // jointe ; c'est la personne qui l'envoie.
    feedbackPanel.prefillFromAssistant(proposal, store.conversationId);
    return;
  }
  taskOpen.value = taskOpen.value === id ? null : id;
}
function taskOf(p: Proposal): TaskProposal {
  return p as TaskProposal;
}
function onCreated(id: string): void {
  added.value = new Set([...added.value, id]);
  taskOpen.value = null;
}

function onKeydown(event: KeyboardEvent): void {
  // Échap ferme d'abord l'écran de validation ouvert par une proposition, pas le tiroir.
  if (event.key === 'Escape' && store.open && !planStore.isOpen) store.hide();
}
// Phase de capture : on voit l'écran de validation encore ouvert avant qu'Échap ne le ferme.
onMounted(() => document.addEventListener('keydown', onKeydown, true));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown, true));
</script>

<template>
  <aside v-if="store.open" class="drawer" aria-label="Assistant" data-testid="assistant-drawer">
    <div class="card-h">
      <div>
        <h2>Assistant Evolusite</h2>
        <p>Claude Opus · lit vos données, n'exécute rien</p>
      </div>
      <div class="row-actions">
        <button
          v-if="store.entries.length"
          class="btn sm"
          type="button"
          :disabled="store.busy"
          @click="store.reset()"
        >
          Nouvelle conversation
        </button>
        <button class="btn sm" type="button" @click="store.hide()">Fermer</button>
      </div>
    </div>
    <div ref="list" class="msgs" aria-live="polite" data-testid="assistant-messages">
      <div class="msg ai">{{ greeting }}</div>
      <div
        v-for="entry in store.entries"
        :key="entry.key"
        class="msg"
        :class="[entry.role === 'user' ? 'me' : 'ai', { err: entry.error }]"
        :data-testid="entry.role === 'assistant' ? 'assistant-answer' : undefined"
        :aria-busy="entry.pending ? 'true' : 'false'"
      >
        <span v-if="entry.status && !entry.text" class="status">{{ entry.status }}</span>
        <template v-else>{{ entry.text }}</template>
        <span v-if="entry.status && entry.text" class="status"> {{ entry.status }}</span>
        <div v-if="entry.proposals.length" class="proposals">
          <template v-for="(p, i) in entry.proposals" :key="i">
            <button
              class="btn sm"
              :class="{ primary: p.kind === 'plan' }"
              type="button"
              :disabled="added.has(`${entry.key}:${i}`)"
              :data-kind="p.kind"
              data-testid="assistant-proposal"
              @click="onProposal(p, `${entry.key}:${i}`)"
            >
              {{ added.has(`${entry.key}:${i}`) ? 'Ajoutée à « À faire »' : p.label }}
            </button>
          </template>
        </div>
        <template v-for="(p, i) in entry.proposals" :key="`f${i}`">
          <AssistantTaskForm
            v-if="p.kind === 'task' && taskOpen === `${entry.key}:${i}`"
            :proposal="taskOf(p)"
            @created="onCreated(`${entry.key}:${i}`)"
            @cancel="taskOpen = null"
          />
        </template>
      </div>
    </div>
    <div class="chat">
      <div v-if="store.suggestions.length" class="sugg" data-testid="assistant-suggestions">
        <button v-for="s in store.suggestions" :key="s" type="button" :disabled="store.busy" @click="send(s)">
          {{ s }}
        </button>
      </div>
      <form class="ask" @submit.prevent="send(question)">
        <input
          ref="input"
          v-model="question"
          type="text"
          placeholder="Pose ta question…"
          aria-label="Question"
          :maxlength="ASSISTANT_MESSAGE_MAX_CHARS"
        />
        <button class="btn primary sm" type="submit" :disabled="store.busy || !question.trim()">
          Envoyer
        </button>
      </form>
    </div>
  </aside>
</template>
